import { useState, useRef, useMemo } from "react";
import { PICWORDS } from "@/data/exercises";
import { Delete } from "lucide-react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function PicWords({ onComplete }) {
  const [round, setRound] = useState(0);
  const [filled, setFilled] = useState([]);
  const [status, setStatus] = useState("idle"); // idle | good
  const correct = useRef(0);
  const wrong = useRef(0);
  const start = useRef(Date.now());

  const current = PICWORDS[round];
  const total = PICWORDS.length;

  const tiles = useMemo(() => {
    return shuffle([...current.word.split(""), ...current.extra]).map((ch, i) => ({ ch, i }));
  }, [round]); // eslint-disable-line react-hooks/exhaustive-deps

  const [used, setUsed] = useState([]);

  const target = current.word.split("");

  const tapTile = (tile) => {
    if (status === "good") return;
    if (used.includes(tile.i)) return;
    const pos = filled.length;
    if (tile.ch === target[pos]) {
      const nf = [...filled, tile.ch];
      setFilled(nf);
      setUsed((u) => [...u, tile.i]);
      if (nf.length === target.length) {
        correct.current += 1;
        setStatus("good");
        setTimeout(() => {
          if (round + 1 >= total) {
            const duration = Math.round((Date.now() - start.current) / 1000);
            const acc = Math.round((correct.current / (correct.current + wrong.current)) * 100) || 100;
            const score = Math.round((correct.current / total) * 100);
            onComplete({ score: Math.max(score, acc - 20), accuracy: acc, duration });
          } else {
            setRound((r) => r + 1);
            setFilled([]);
            setUsed([]);
            setStatus("idle");
          }
        }, 900);
      }
    } else {
      wrong.current += 1;
    }
  };

  const backspace = () => {
    if (status === "good" || filled.length === 0) return;
    setFilled((f) => f.slice(0, -1));
    setUsed((u) => u.slice(0, -1));
  };

  return (
    <div className="w-full max-w-2xl mx-auto text-center" data-testid="picwords-game">
      <p className="text-lg font-care text-emerald-700 font-bold mb-4">
        Mot {round + 1} sur {total}
      </p>
      <div className="w-56 h-56 mx-auto rounded-3xl overflow-hidden border-4 border-amber-200 mb-6">
        <img src={current.img} alt="indice" className="w-full h-full object-cover" />
      </div>

      <div className="flex justify-center gap-2 sm:gap-3 mb-8 flex-wrap">
        {target.map((_, i) => (
          <div
            key={i}
            data-testid={`picwords-slot-${i}`}
            className={`w-12 h-14 sm:w-14 sm:h-16 rounded-xl border-4 flex items-center justify-center font-patient font-black text-3xl ${
              status === "good" ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-emerald-700/20 bg-white text-gray-800"
            }`}
          >
            {filled[i] || ""}
          </div>
        ))}
      </div>

      <div className="flex justify-center gap-3 flex-wrap mb-6">
        {tiles.map((tile) => (
          <button
            key={tile.i}
            onClick={() => tapTile(tile)}
            disabled={used.includes(tile.i)}
            data-testid={`picwords-tile-${tile.ch}-${tile.i}`}
            className={`w-14 h-14 rounded-2xl font-patient font-black text-2xl border-4 transition-transform duration-150 active:scale-90 ${
              used.includes(tile.i)
                ? "opacity-30 border-gray-200 bg-gray-100 text-gray-400"
                : "border-emerald-700/20 bg-[#F8E4C7] text-gray-800"
            }`}
          >
            {tile.ch}
          </button>
        ))}
      </div>

      <button
        onClick={backspace}
        data-testid="picwords-backspace"
        className="inline-flex items-center gap-2 px-6 h-14 rounded-2xl bg-white border-4 border-emerald-700/20 font-patient font-bold text-lg text-gray-600 active:scale-95 transition-transform duration-150"
      >
        <Delete className="w-6 h-6" /> Effacer
      </button>
    </div>
  );
}
